import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { ORG } from "../lib/constants";
import {
  ArrowRight,
  Check,
  CalendarClock,
  BookOpenCheck,
  HeartHandshake,
  ShieldCheck,
  Users,
  Briefcase,
} from "lucide-react";

export default function ProgramsPage() {
  const expectations = [
    "Maintain positive attitudes",
    "Pass weekly and random drug tests",
    "Obtain employment",
    "Attend 1 church service weekly",
    "Attend 1 Bible Study weekly",
    "Attend 1 Celebrate Recovery weekly",
    "Attend counseling",
    "Respect the house, others, and shared spaces",
    "No foul or offensive language permitted",
  ];

  return (
    <div data-testid="programs-page">
      <section className="pt-20 pb-16 md:pt-28 md:pb-24 bg-gradient-to-br from-brand-cream via-brand-bone/30 to-brand-cream">
        <div className="container-editorial">
          <Reveal>
            <div className="max-w-3xl">
              <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">Our Program</div>
              <h1 className="mt-3 font-serif text-5xl md:text-6xl lg:text-7xl text-brand-charcoal tracking-tight leading-[1.02]">
                A year that changes everything.
              </h1>
              <p className="mt-6 text-lg text-brand-muted leading-relaxed">
                The Hosea House is a faith-based transitional home providing assistance and support for women who
                have been incarcerated or are recovering from substance abuse. Our program runs one year — then we
                continue with aftercare.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-20">
        <div className="container-editorial">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: CalendarClock, title: "One-year program", body: "A dedicated year to heal, grow, and establish lasting habits." },
              { icon: HeartHandshake, title: "Aftercare built in", body: "We continue walking with graduates beyond the program year." },
              { icon: ShieldCheck, title: "Safe & structured home", body: "House manager on site; clear rules; faithful sisterhood." },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 80}>
                <div className="h-full bg-white rounded-2xl p-7 border border-brand-line hover:-translate-y-1 hover:shadow-lg transition-all duration-500">
                  <span className="w-11 h-11 rounded-xl bg-brand-teal/10 text-brand-teal flex items-center justify-center">
                    <c.icon className="w-5 h-5" />
                  </span>
                  <h3 className="mt-5 font-serif text-2xl text-brand-charcoal">{c.title}</h3>
                  <p className="mt-3 text-brand-muted leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Expectations */}
      <section className="py-20 bg-white border-y border-brand-line">
        <div className="container-editorial grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">Program Rules & Expectations</div>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl text-brand-charcoal tracking-tight leading-tight">
              What we ask of every resident.
            </h2>
            <p className="mt-5 text-brand-muted leading-relaxed">
              Alongside our house rules, Hosea Ministries asks every resident to commit to the following rhythms.
              They are not obstacles — they are the scaffolding of real restoration.
            </p>
          </Reveal>
          <Reveal delay={120} className="md:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-3">
              {expectations.map((e) => (
                <li
                  key={e}
                  className="flex items-start gap-3 bg-brand-cream rounded-xl px-4 py-3 border border-brand-line"
                >
                  <Check className="w-5 h-5 text-brand-teal mt-0.5 shrink-0" />
                  <span className="text-sm text-brand-charcoal">{e}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Fees */}
      <section className="py-24">
        <div className="container-editorial">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2rem] bg-brand-teal text-brand-cream px-8 md:px-14 py-14 md:py-20">
              <div className="absolute -top-28 -right-24 w-[420px] h-[420px] rounded-full bg-brand-peach/15 blur-3xl" />
              <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                <div className="md:col-span-7">
                  <div className="text-[11px] tracking-[0.24em] uppercase text-brand-peach font-semibold">Program Fees</div>
                  <h2 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight leading-tight">
                    Clear, simple, transparent.
                  </h2>
                  <p className="mt-5 text-brand-cream/80 leading-relaxed max-w-xl">
                    We keep fees modest so we can keep our doors open to the women who need us most. Please allow
                    72 hours for our application process.
                  </p>
                </div>
                <div className="md:col-span-5">
                  <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 space-y-4">
                    <div className="flex items-baseline justify-between">
                      <div>
                        <div className="text-[11px] tracking-[0.2em] uppercase text-brand-peach">Entry fee</div>
                        <div className="font-serif text-3xl mt-1">${ORG.fees.entry}</div>
                      </div>
                      <Briefcase className="w-5 h-5 text-brand-peach" />
                    </div>
                    <div className="h-px bg-white/10" />
                    <div className="flex items-baseline justify-between">
                      <div>
                        <div className="text-[11px] tracking-[0.2em] uppercase text-brand-peach">Weekly</div>
                        <div className="font-serif text-3xl mt-1">${ORG.fees.weekly}</div>
                      </div>
                      <Users className="w-5 h-5 text-brand-peach" />
                    </div>
                  </div>
                  <div className="mt-6">
                    <Link
                      to="/apply"
                      data-testid="programs-apply-btn"
                      className="inline-flex items-center gap-2 bg-brand-peach text-brand-charcoal rounded-full px-6 py-3 font-medium hover:bg-white transition-all hover:-translate-y-0.5"
                    >
                      Begin the application <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What we cover */}
      <section className="py-20 bg-brand-bone/40">
        <div className="container-editorial">
          <Reveal>
            <div className="max-w-2xl">
              <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">What's Included</div>
              <h2 className="mt-3 font-serif text-4xl md:text-5xl text-brand-charcoal tracking-tight">
                Care for the whole person.
              </h2>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: BookOpenCheck, title: "Bible study & counseling", body: "Weekly Bible study, church, Celebrate Recovery, and professional counseling." },
              { icon: HeartHandshake, title: "Life & parenting skills", body: "Housekeeping, budgeting, parenting, and the daily rhythms of healthy life." },
              { icon: Users, title: "Community & resources", body: "Connections to jobs, education, and childcare through community partners." },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 80}>
                <div className="h-full bg-white rounded-2xl p-7 border border-brand-line hover:-translate-y-1 hover:shadow-lg transition-all duration-500">
                  <span className="w-11 h-11 rounded-xl bg-brand-teal/10 text-brand-teal flex items-center justify-center">
                    <c.icon className="w-5 h-5" />
                  </span>
                  <h3 className="mt-5 font-serif text-2xl text-brand-charcoal">{c.title}</h3>
                  <p className="mt-3 text-brand-muted leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
